import { useEffect, useRef } from 'react';

type EscapeHandler = (event: KeyboardEvent) => void;
interface Layer {
  handler: { current: EscapeHandler };
}

/**
 * Open overlays, oldest first. Escape goes to the last one only.
 *
 * Each overlay used to add its own `keydown` listener on `document`, so with
 * a confirmation open over a drawer, or a select open inside one, a single
 * Escape closed every layer at once (listeners fire in the order they were
 * added, and none of them knew about the others).
 */
const layers: Layer[] = [];

function onKeyDown(event: KeyboardEvent): void {
  // Something closer to the focus already handled this Escape: a Radix menu
  // (it calls preventDefault when it dismisses), a combobox, an editor.
  if (event.key !== 'Escape' || event.defaultPrevented || event.isComposing) return;
  const top = layers[layers.length - 1];
  if (!top) return;
  event.preventDefault();
  top.handler.current(event);
}

/**
 * Registers an overlay layer while `active`. Escape calls the handler of the
 * most recently opened layer and nobody else's. A layer that should not close
 * on Escape still registers (pass a handler that does nothing), so Escape
 * never reaches whatever is underneath it.
 */
export function useEscapeLayer(handler: EscapeHandler, active: boolean): void {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    if (!active) return undefined;
    const layer: Layer = { handler: handlerRef };
    layers.push(layer);
    if (layers.length === 1) document.addEventListener('keydown', onKeyDown);
    return () => {
      const index = layers.indexOf(layer);
      if (index >= 0) layers.splice(index, 1);
      if (layers.length === 0) document.removeEventListener('keydown', onKeyDown);
    };
  }, [active]);
}
